/**
 * Ops -> DirtyScopes (W1-03 core-model-ops; contract core/ops/types.ts `DirtyScopes`).
 *
 * What a sequence of ops changed, for incremental consumers (engine dirty DAG, timeline redraw, checker,
 * inspector selectors). Records are resolved against the document BEFORE the ops plus the records the sequence
 * itself inserted or patched so far: a set on a key finds its track (and the track's line) even when the track
 * was created earlier in the same sequence; a moved note dirties both its old and its new line; a key moved to
 * another track dirties both owners. Effect and video variable tracks dirty `effects`.
 */

import { applyPatch } from './equal';
import type { DirtyScopes, Op } from './types';
import type { ChartDoc, Key, KeyId, LineId, LineProp, Note, NoteId, Track, TrackId, TrackOwner } from '../model/types';

interface MutDirty {
  tempo: boolean;
  timing: boolean;
  meta: boolean;
  pkg: boolean;
  editor: boolean;
  rpeRoot: boolean;
  lineSet: boolean;
  lineProps: Set<LineId>;
  lineMotion: Map<LineId, Set<LineProp>>;
  lineNotes: Set<LineId>;
  effects: boolean;
  markers: boolean;
  groups: boolean;
  assets: boolean;
  generators: boolean;
}

function mutEmpty(): MutDirty {
  return {
    tempo: false,
    timing: false,
    meta: false,
    pkg: false,
    editor: false,
    rpeRoot: false,
    lineSet: false,
    lineProps: new Set(),
    lineMotion: new Map(),
    lineNotes: new Set(),
    effects: false,
    markers: false,
    groups: false,
    assets: false,
    generators: false,
  };
}

const EMPTY: DirtyScopes = Object.freeze({
  tempo: false,
  timing: false,
  meta: false,
  pkg: false,
  editor: false,
  rpeRoot: false,
  lineSet: false,
  lineProps: new Set<LineId>(),
  lineMotion: new Map<LineId, ReadonlySet<LineProp>>(),
  lineNotes: new Set<LineId>(),
  effects: false,
  markers: false,
  groups: false,
  assets: false,
  generators: false,
});

export function emptyDirty(): DirtyScopes {
  return EMPTY;
}

/** True when nothing is dirty. */
export function isDirtyEmpty(d: DirtyScopes): boolean {
  return (
    !d.tempo &&
    !d.timing &&
    !d.meta &&
    !d.pkg &&
    !d.editor &&
    !d.rpeRoot &&
    !d.lineSet &&
    d.lineProps.size === 0 &&
    d.lineMotion.size === 0 &&
    d.lineNotes.size === 0 &&
    !d.effects &&
    !d.markers &&
    !d.groups &&
    !d.assets &&
    !d.generators
  );
}

export function mergeDirty(a: DirtyScopes, b: DirtyScopes): DirtyScopes {
  if (isDirtyEmpty(b)) return a;
  if (isDirtyEmpty(a)) return b;
  const lineMotion = new Map<LineId, Set<LineProp>>();
  for (const src of [a.lineMotion, b.lineMotion]) {
    for (const [line, props] of src) {
      let set = lineMotion.get(line);
      if (set === undefined) {
        set = new Set();
        lineMotion.set(line, set);
      }
      for (const p of props) set.add(p);
    }
  }
  return {
    tempo: a.tempo || b.tempo,
    timing: a.timing || b.timing,
    meta: a.meta || b.meta,
    pkg: a.pkg || b.pkg,
    editor: a.editor || b.editor,
    rpeRoot: a.rpeRoot || b.rpeRoot,
    lineSet: a.lineSet || b.lineSet,
    lineProps: new Set([...a.lineProps, ...b.lineProps]),
    lineMotion,
    lineNotes: new Set([...a.lineNotes, ...b.lineNotes]),
    effects: a.effects || b.effects,
    markers: a.markers || b.markers,
    groups: a.groups || b.groups,
    assets: a.assets || b.assets,
    generators: a.generators || b.generators,
  };
}

/** Records as the sequence leaves them so far (null = deleted), falling back to `before`. */
class Resolver<K, V extends object> {
  private readonly local = new Map<K, V | null>();
  private readonly base: ReadonlyMap<K, V>;

  constructor(base: ReadonlyMap<K, V>) {
    this.base = base;
  }

  get(id: K): V | undefined {
    if (this.local.has(id)) return this.local.get(id) ?? undefined;
    return this.base.get(id);
  }

  put(id: K, rec: V | null): void {
    this.local.set(id, rec);
  }

  patch(id: K, next: object): void {
    const cur = this.get(id);
    if (cur !== undefined) this.local.set(id, applyPatch(cur, next));
  }
}

function markOwner(d: MutDirty, owner: TrackOwner | undefined): void {
  if (owner === undefined) return;
  if (owner.kind !== 'line') {
    d.effects = true;
    return;
  }
  let set = d.lineMotion.get(owner.line);
  if (set === undefined) {
    set = new Set();
    d.lineMotion.set(owner.line, set);
  }
  set.add(owner.prop);
}

export function dirtyOf(ops: readonly Op[], before: ChartDoc): DirtyScopes {
  if (ops.length === 0) return EMPTY;
  const d = mutEmpty();
  const tracks = new Resolver<TrackId, Track>(before.tracks);
  const keys = new Resolver<KeyId, Key>(before.keys);
  const notes = new Resolver<NoteId, Note>(before.notes);

  for (const op of ops) {
    switch (op.o) {
      case 'idSeq':
        break;
      case 'order':
        if (op.list === 'lineOrder') d.lineSet = true;
        else d.effects = true;
        break;
      case 'single':
        switch (op.key) {
          case 'meta':
            d.meta = true;
            break;
          case 'pkg':
            d.pkg = true;
            if (Object.prototype.hasOwnProperty.call(op.next, 'infoOffsetSec')) d.timing = true;
            break;
          case 'timing':
            d.timing = true;
            break;
          case 'editor':
            d.editor = true;
            break;
          case 'rpeRoot':
            d.rpeRoot = true;
            break;
        }
        break;
      case 'ins':
      case 'del':
      case 'set':
        switch (op.tbl) {
          case 'bpm':
            d.tempo = true;
            break;
          case 'lines': {
            const id = op.o === 'set' ? op.id : op.rec.id;
            d.lineProps.add(id);
            if (op.o !== 'set') d.lineSet = true;
            break;
          }
          case 'tracks': {
            if (op.o === 'set') {
              markOwner(d, tracks.get(op.id)?.owner);
              tracks.patch(op.id, op.next);
              markOwner(d, tracks.get(op.id)?.owner);
            } else {
              markOwner(d, op.rec.owner);
              tracks.put(op.rec.id, op.o === 'ins' ? op.rec : null);
            }
            break;
          }
          case 'keys': {
            if (op.o === 'set') {
              const old = keys.get(op.id);
              if (old !== undefined) markOwner(d, tracks.get(old.track)?.owner);
              keys.patch(op.id, op.next);
              const now = keys.get(op.id);
              if (now !== undefined && now.track !== old?.track) markOwner(d, tracks.get(now.track)?.owner);
            } else {
              markOwner(d, tracks.get(op.rec.track)?.owner);
              keys.put(op.rec.id, op.o === 'ins' ? op.rec : null);
            }
            break;
          }
          case 'notes': {
            if (op.o === 'set') {
              const old = notes.get(op.id);
              if (old !== undefined) d.lineNotes.add(old.line);
              notes.patch(op.id, op.next);
              const now = notes.get(op.id);
              if (now !== undefined) d.lineNotes.add(now.line);
            } else {
              d.lineNotes.add(op.rec.line);
              notes.put(op.rec.id, op.o === 'ins' ? op.rec : null);
            }
            break;
          }
          case 'effects':
          case 'videos':
            d.effects = true;
            break;
          case 'markers':
            d.markers = true;
            break;
          case 'groups':
            d.groups = true;
            break;
          case 'assets':
            d.assets = true;
            break;
          case 'generators':
            d.generators = true;
            break;
        }
        break;
    }
  }
  return d;
}
